import { postJson } from "../json_request"
import { StepError } from "../config_tool_step"

// Treiber von prepare_chip für die STM32WB5x (dmct.md #21, #22). Die Shell bietet nur stlink.exec (beliebige
// STM32_Programmer_CLI-Aufrufe ohne -c) und files; alle Adressen, Befehle und die Reihenfolge stehen hier und kommen mit
// DM. Reihenfolge: UID lesen → Platine anmelden → FUS starten, Versionen lesen → FUS-Upgrade nur wenn älter als der Pin
// → Stack installieren wenn er fehlt oder vom Pin abweicht (auch ein neuerer: App und Loader eines alten Auftrags
// gehören zu seinem Stack) → Schlüssel speichern wenn nötig → Stack starten, nSWBOOT0=1.
// Jede FUS-Operation schreibt ST's FUS-Operator über den Jumper bei 0x08000000, setzt nSWBOOT0=0 und lässt CPU2 im
// FUS-Modus: deshalb kommen Stackstart und nSWBOOT0=1 zuletzt, und die Anleitung flasht den Jumper danach.

// Register UID64 (UID64_BASE): +0 UDN, +4 Device-ID und ST-Company-ID; als 64-Bit-Wert hohes Wort zuerst.
const UID64_ADDRESS = "0x1FFF7580"
// Ergebnisblock des FUS-Operators im SRAM1 (übersteht den Reset beim Verbinden): FUS-Version bei +0x10, Stack bei
// +0x14. Undokumentiert (so liest ihn auch die GUI); geprüft wird nur, dass eine FUS-Version dasteht, denn FUS ist auf
// jedem Chip installiert. Fehlt sie, hat sich das Layout geändert.
const FUS_BLOCK_ADDRESS = 0x20010000
const MIN_FUS = [1, 2, 0]

// "0x20010000 : 00000000 00000500 …" → Map Adresse → Wort (Grossbuchstaben)
export function parseR32(stdout) {
  const words = new Map()
  for (const [, address, rest] of stdout.matchAll(/0x([0-9a-f]{8})\s*:\s*((?:[0-9a-f]{8}\s*)+)/gi)) {
    rest
      .trim()
      .split(/\s+/)
      .forEach((word, i) => words.set(parseInt(address, 16) + i * 4, word.toUpperCase()))
  }
  return words
}

export function parseUid64(stdout) {
  const words = parseR32(stdout)
  const low = words.get(parseInt(UID64_ADDRESS, 16))
  const high = words.get(parseInt(UID64_ADDRESS, 16) + 4)
  if (!low || !high) throw new Error(`No UID64 in the STM32_Programmer_CLI output:\n${stdout.trim()}`)
  return high + low
}

// Versionswort: major[31:24] minor[23:16] sub[15:8], unteres Byte Branch/Typ. 0 = nicht installiert.
export function versionFromWord(word) {
  const value = parseInt(word, 16)
  if (!value) return null
  return [value >>> 24, (value >>> 16) & 0xff, (value >>> 8) & 0xff]
}

export function compareVersions(a, b) {
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] - b[i]
  return 0
}

// Chip-UID über SWD. mode "HOTPLUG" liest ohne Reset, z.B. während der HW-Test läuft (serial_test vor <LA>).
export async function readUidStm32wb5x(bridge, mode = "UR") {
  return parseUid64(await bridge.stlink.exec({ args: ["-r32", UID64_ADDRESS, "8"], mode }))
}

const parseVersion = (text) => text.split(".").map(Number)
const versionText = (version) => version.join(".")

export async function prepareStm32wb5x({ bridge, run, modules, log, say }) {
  const exec = (args, mode) => bridge.stlink.exec({ args, mode })
  const fusModule = modules.stm32wb_fus
  const stackModule = modules.stm32wb_wireless_stack

  log(say("reading_uid"))
  const uid = await readUidStm32wb5x(bridge)
  run.board = await postJson(run.boardsUrlValue, { mcu_uid: uid })
  log(say("registered", { uid }))
  const boardUrl = `${run.boardsUrlValue}/${uid}`

  const report = async (reason, words) => {
    await postJson(`${boardUrl}/wireless_check_failed`, { reason, fus_word: words.fus, stack_word: words.stack }).catch(() => {})
  }

  const readVersions = async () => {
    log(say("starting_fus"))
    try {
      await exec(["-startfus"])
    } catch (error) {
      // Läuft der Stack, schaltet der erste Aufruf CPU2 nur auf FUS um und meldet einen Fehler; der zweite findet FUS.
      if (!/not yet running|FUS_STATE_NOT_RUNNING|FUS_STATE_ERR_UNKNOWN/i.test(error.message)) throw error
      log(say("fus_retry"))
      await exec(["-startfus"])
    }
    const words = parseR32(await exec(["-r32", `0x${FUS_BLOCK_ADDRESS.toString(16)}`, "32"]))
    const raw = { fus: words.get(FUS_BLOCK_ADDRESS + 0x10), stack: words.get(FUS_BLOCK_ADDRESS + 0x14) }
    const fus = raw.fus && versionFromWord(raw.fus)
    if (!fus) {
      await report("unknown_version_layout", raw)
      throw new StepError("unknown_layout", say("unknown_layout"))
    }
    const versions = { fus, stack: versionFromWord(raw.stack ?? "0"), raw }
    log(say("versions", { fus: versionText(fus), stack: versions.stack ? versionText(versions.stack) : say("no_stack") }))
    return versions
  }

  // Modul-Binary über FUS installieren; die Datei kommt über die Shell von DM und wird gleich wieder gelöscht.
  const install = async (module, firstInstall) => {
    const file = await bridge.files.download({ url: module.url, filename: module.filename })
    try {
      await exec(["-fwupgrade", file, module.address, `firstinstall=${firstInstall ? 1 : 0}`])
    } finally {
      await bridge.files.remove(file).catch(() => {})
    }
  }

  let versions = await readVersions()

  const pinnedFus = parseVersion(fusModule.version)
  if (compareVersions(versions.fus, MIN_FUS) < 0) {
    await report("fus_too_old", versions.raw)
    throw new StepError("fus_too_old", say("fus_too_old", { board: versionText(versions.fus) }))
  }
  if (compareVersions(versions.fus, pinnedFus) >= 0) {
    log(say("fus_skip", { board: versionText(versions.fus), pinned: fusModule.version }))
  } else {
    if (run.board.deveui) throw new StepError("fus_on_device", say("fus_on_device"))
    log(say("fus_upgrading", { board: versionText(versions.fus), pinned: fusModule.version }))
    await install(fusModule, false)
    // Das Upgrade löscht alle Schlüssel im CKS (AN5185) und kann den Stack mitnehmen: Schlüssel neu, Versionen neu.
    run.board = { ...run.board, ...(await postJson(`${boardUrl}/key_erased`)) }
    log(say("fus_upgraded"))
    versions = await readVersions()
  }

  const pinnedStack = parseVersion(stackModule.version)
  if (!versions.stack) {
    log(say("stack_installing", { pinned: stackModule.version }))
    await install(stackModule, true)
  } else if (compareVersions(versions.stack, pinnedStack) !== 0) {
    // Auch ein neuerer Stack wird ersetzt: App und Loader des Auftrags gehören zu genau diesem Stack (Tobias 2026-10-28).
    // Ein Downgrade geht, solange niemand Anti-Rollback aktiviert hat (die Shell verweigert -antirollback).
    log(say("stack_replacing", { board: versionText(versions.stack), pinned: stackModule.version }))
    await install(stackModule, false)
  } else {
    log(say("stack_skip", { board: versionText(versions.stack), pinned: stackModule.version }))
  }

  if (run.board.key_stored) {
    log(say("key_skip"))
  } else {
    log(say("key_writing"))
    const file = await bridge.files.download({ url: `${location.origin}${boardUrl}/device_key`, filename: "k_dev.bin" })
    try {
      await exec(["-wusrkey", file, "keytype=1"])
    } finally {
      await bridge.files.remove(file).catch(() => {})
    }
    await postJson(`${boardUrl}/key_stored`)
    run.board = { ...run.board, key_stored: true }
    log(say("key_done"))
  }

  log(say("starting_stack"))
  await exec(["-startwirelessstack"])
  await exec(["-ob", "nSWBOOT0=1"])
  log(say("done"))
}
